import React from 'react';
import { Chip, Grid } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { GridColDef } from '@mui/x-data-grid';
import PageHeader from '../../../components/page-header';
import { FilterPanel } from '../../../components/filter-panel';
import { RowAction } from '../../../components/row-action';
import { TwoLineCell } from '../../../components/two-line-cell';
import { usePagedList } from '../../../hooks/use-paged-list';
import { formatDate } from '../../../utils/date';
import { usePublicAccountSearchController } from './context/public-account-search-controller';
import { usePublicAccountSearchState } from './context/public-account-search-context';
import PublicAccountAppService from '../../../application/platform/apm/public-account/public-account-app-service';
import { IPublicAccountListResponse } from '../../../model/apm/public-account/response/public-account-list-response';
import { FilterInput, FilterSelect } from '../../../components/filter-fields';
import { lookupOptions } from '../../../utils/filter-options';
import { PagedGrid } from '../../../components/paged-grid';
import { AttachmentsAction, DeleteAction, EditAction } from '../../../components/row-actions';
import { PublicAccountDrawer } from './components/public-account-drawer';
import { subcategoryOptions } from './components/public-account-category-utils';

export function PublicAccountSearchComponent() {
    const ctrl = usePublicAccountSearchController();
    const { parameters, searchTrigger, categories, documentTypes, drawer } = usePublicAccountSearchState();
    const list = usePagedList(PublicAccountAppService.list, parameters, searchTrigger, 'Não foi possível carregar as publicações');
    const filterSubcategories = subcategoryOptions(categories, parameters.categoryId).map((s) => ({ value: s.id, label: s.label }));

    // Publicações vindas do Diário Oficial são somente leitura
    function renderActions(row: IPublicAccountListResponse) {
        const readOnly = row.isFromOfficialDiary;
        return (
            <React.Fragment>
                <AttachmentsAction fileCount={row.fileCount} emptyTitle={readOnly ? 'Sem documentos' : undefined} onClick={() => ctrl.openDrawer(row)} />
                {readOnly
                    ? <RowAction title="Visualizar" onClick={() => ctrl.openDrawer(row)}><VisibilityIcon fontSize="small" /></RowAction>
                    : <EditAction onClick={() => ctrl.openDrawer(row)} />}
                <DeleteAction
                    title={readOnly ? 'Publicações do Diário Oficial não podem ser excluídas por aqui' : 'Excluir'}
                    disabled={readOnly}
                    onClick={() => ctrl.handleDelete(row)}
                />
            </React.Fragment>
        );
    }

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 80 },
        {
            field: 'description', headerName: 'Publicação', flex: 1, minWidth: 280,
            renderCell: (params) => (
                <TwoLineCell primary={params.value} secondary={[params.row.category, params.row.subcategory].filter(Boolean).join(' › ')} bold />
            )
        },
        { field: 'date', headerName: 'Publicada em', width: 130, valueFormatter: (params) => formatDate(params.value) },
        {
            field: 'isFromOfficialDiary', headerName: 'Origem', width: 150,
            renderCell: (params) => params.value
                ? <Chip size="small" color="info" variant="outlined" icon={<MenuBookIcon />} label="Diário Oficial" />
                : <Chip size="small" variant="outlined" label="Cadastro manual" />
        },
        { field: 'actions', headerName: 'Ações', width: 130, sortable: false, renderCell: (params) => renderActions(params.row) }
    ];

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Contas Públicas"
                    subtitle="Publicações de contas públicas e seus documentos exibidos no portal"
                    breadcrumbs={[{ label: 'Contas Públicas' }]}
                />
            </Grid>
            <Grid item xs={12}>
                <FilterPanel onClear={ctrl.handleClearFilters} onSearch={ctrl.handleSearch}>
                    <Grid item xs={12} md={4}>
                        <FilterInput label="Descrição" name="input-term" value={parameters.term} onChange={ctrl.handleOnChange} onSearch={ctrl.handleSearch} />
                    </Grid>
                    <Grid item xs={12} sm={5} md={3}>
                        <FilterSelect label="Categoria" name="select-category" value={parameters.categoryId} options={lookupOptions(categories)} emptyLabel="Todas" onChange={ctrl.handleOnChange} />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <FilterSelect label="Subcategoria" name="select-subcategory" value={parameters.subcategoryId} options={filterSubcategories} emptyLabel="Todas" onChange={ctrl.handleOnChange} />
                    </Grid>
                    <Grid item xs={12} sm={3} md={2}>
                        <FilterInput label="Ano de publicação" name="input-year" type="number" value={parameters.year} onChange={ctrl.handleOnChange} onSearch={ctrl.handleSearch} />
                    </Grid>
                </FilterPanel>
            </Grid>
            <Grid item xs={12}>
                <PagedGrid
                    columns={columns}
                    list={list}
                    parameters={parameters}
                    onPaginationChange={ctrl.handlePaginationChange}
                    addLabel="Nova Publicação"
                    onAdd={() => ctrl.openDrawer()}
                    rowHeight={60}
                />
            </Grid>

            <PublicAccountDrawer
                open={drawer.open}
                initial={drawer.publicAccount}
                files={drawer.files}
                categories={categories}
                documentTypes={documentTypes}
                isSaving={ctrl.isSaving}
                onClose={ctrl.closeDrawer}
                onSave={ctrl.handleSave}
                onUploadFile={ctrl.handleUploadFile}
                onDeleteFile={ctrl.handleDeleteFile}
            />
        </React.Fragment>
    );
}
